/**
 * Override-payment-status flow split across two pieces:
 *
 *  - `useOverridePaymentStatusFlow(order)` — owns the picked-status state, the
 *    mutation, the toast wiring, and returns an `openConfirm(status)` callback
 *    plus a `modal` ReactNode. The caller renders the modal at its own level.
 *  - `<OverridePaymentStatusMenu>` and `<OverridePaymentStatusButton>` — the
 *    two trigger UIs, both of which take the `onPick` callback from the hook.
 *
 * Why the modal is hoisted to the caller: in OrderViewV2 the menu trigger
 * sits inside the outer Actions Menu.Dropdown. Picking a status closes the
 * outer menu, which unmounts everything inside the dropdown — including the
 * modal if it lived alongside the trigger. Hoisting the modal to the caller's
 * top-level JSX keeps it rendered across the menu's open/close cycle.
 *
 * V1 trigger note: RpiButton is a plain function component (no forwardRef), so
 * Mantine v5's `Menu.Target` cloneElement-based ref injection is silently
 * dropped and the dropdown can't anchor to it. The V1 button is wrapped in a
 * `<div className={classes.triggerWrapper}>` so Mantine has a real DOM node
 * to attach to; the click still bubbles from the button to the wrapper.
 */
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { IconV3, RpiButton, useToasts } from "@rpi/openapi-core";
import { createStyles, Menu, Stack } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Body } from "../../components/Typography";
import { ConfirmationModalV2 } from "../../components/modals/ConfirmationModalV2";

const useStyles = createStyles({
  // Mantine Menu.Target needs a real DOM node to attach its ref to. RpiButton
  // is a function component without forwardRef, so we wrap it in this div.
  // Inline-flex keeps the wrapper sized to its child and not a full-width block.
  triggerWrapper: {
    display: "inline-flex",
  },
});

const PAYMENT_STATUS_LABELS: Record<GraphQL.PaymentStatus, string> = {
  [GraphQL.PaymentStatus.Paid]: "Paid",
  [GraphQL.PaymentStatus.Unpaid]: "Unpaid",
  [GraphQL.PaymentStatus.PaymentPending]: "Payment pending",
  [GraphQL.PaymentStatus.ExternallyPaid]: "Externally paid",
  [GraphQL.PaymentStatus.NoPaymentStaffOrder]: "No payment (staff order)",
};

const PAYMENT_STATUS_ENTRIES: ReadonlyArray<{ value: GraphQL.PaymentStatus; label: string }> = [
  { value: GraphQL.PaymentStatus.Paid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.Paid] },
  { value: GraphQL.PaymentStatus.Unpaid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.Unpaid] },
  { value: GraphQL.PaymentStatus.PaymentPending, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.PaymentPending] },
  { value: GraphQL.PaymentStatus.ExternallyPaid, label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.ExternallyPaid] },
  {
    value: GraphQL.PaymentStatus.NoPaymentStaffOrder,
    label: PAYMENT_STATUS_LABELS[GraphQL.PaymentStatus.NoPaymentStaffOrder],
  },
];

const OVERRIDE_REASON = "Manual override from admin dashboard";

type Order = NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>;

type OverridableOrder = Pick<Order, "customerOrderId" | "paymentStatus"> & {
  customer?: Pick<NonNullable<Order["customer"]>, "publicId"> | null;
};

interface ConfirmBodyProps {
  customerOrderId: string;
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  newStatus: GraphQL.PaymentStatus;
}

const ConfirmBody: React.FC<ConfirmBodyProps> = ({ customerOrderId, currentStatus, newStatus }) => (
  <Stack spacing="sm">
    <Body>
      Override payment status on order <strong>{customerOrderId}</strong>?
    </Body>
    <Body>
      Current status: <strong>{currentStatus ? PAYMENT_STATUS_LABELS[currentStatus] : "Unknown"}</strong>
    </Body>
    <Body>
      New status: <strong>{PAYMENT_STATUS_LABELS[newStatus]}</strong>
    </Body>
    <Body>This action will be recorded in the audit log along with your user.</Body>
  </Stack>
);

export function useOverridePaymentStatusFlow(order: OverridableOrder) {
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();
  const [pickedStatus, setPickedStatus] = React.useState<GraphQL.PaymentStatus | null>(null);
  const [error, setError] = React.useState<string | undefined>();

  const { mutate, isLoading } = GraphQL.useAdminOverridePaymentStatusMutation({
    onSuccess: async (data) => {
      const updatedStatus = data.adminOverridePaymentStatus?.paymentStatus;
      const label = updatedStatus ? PAYMENT_STATUS_LABELS[updatedStatus] : "the new value";
      showSuccessToast(`Payment status updated to ${label}`);
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
      setPickedStatus(null);
      setError(undefined);
    },
    onError: (err) => {
      const message = isGraphQLError(err) ? err.message : "Failed to update payment status";
      setError(message);
      showErrorToast(message);
    },
  });

  const openConfirm = (status: GraphQL.PaymentStatus) => {
    setError(undefined);
    setPickedStatus(status);
  };

  const closeConfirm = () => {
    if (isLoading) return;
    setPickedStatus(null);
    setError(undefined);
  };

  const handleConfirm = () => {
    if (!pickedStatus || !order.customer?.publicId || !order.customerOrderId) return;
    mutate({
      input: {
        orderIdentifier: {
          customerOrderId: order.customerOrderId,
          customerPublicId: order.customer.publicId,
        },
        newPaymentStatus: pickedStatus,
        reason: OVERRIDE_REASON,
      },
    });
  };

  const modal = (
    <ConfirmationModalV2
      opened={pickedStatus !== null}
      onClose={closeConfirm}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      error={error}
      title="Override payment status"
      message={
        pickedStatus !== null && order.customerOrderId ? (
          <ConfirmBody
            customerOrderId={order.customerOrderId}
            currentStatus={order.paymentStatus}
            newStatus={pickedStatus}
          />
        ) : null
      }
      confirmLabel="Override"
      cancelLabel="Cancel"
      loadingLabel="Overriding..."
    />
  );

  return { openConfirm, modal };
}

interface StatusItemListProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

const StatusItemList: React.FC<StatusItemListProps> = ({ currentStatus, onPick }) => (
  <>
    {PAYMENT_STATUS_ENTRIES.map(({ value, label }) => {
      const isCurrent = value === currentStatus;
      return (
        <Menu.Item
          key={value}
          data-testid={`override-payment-status-${value}`}
          disabled={isCurrent}
          onClick={() => onPick(value)}
        >
          {isCurrent ? `${label} (current)` : label}
        </Menu.Item>
      );
    })}
  </>
);

interface OverridePaymentStatusMenuProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

/** Used inside OrderViewV2's existing Menu.Dropdown — hover-opens a nested submenu. */
export const OverridePaymentStatusMenu: React.FC<OverridePaymentStatusMenuProps> = ({ currentStatus, onPick }) => (
  <Menu trigger="hover" position="right-start" closeOnItemClick withinPortal>
    <Menu.Target>
      <Menu.Item
        data-testid="override-payment-status-menu-item"
        icon={<IconV3 icon="edit" size={20} aria-hidden="true" />}
        rightSection={<IconV3 icon="chevron_right" size={16} aria-hidden="true" />}
        closeMenuOnClick={false}
        role="menuitem"
      >
        Override payment status
      </Menu.Item>
    </Menu.Target>
    <Menu.Dropdown>
      <StatusItemList currentStatus={currentStatus} onPick={onPick} />
    </Menu.Dropdown>
  </Menu>
);

interface OverridePaymentStatusButtonProps {
  currentStatus: GraphQL.PaymentStatus | null | undefined;
  onPick: (status: GraphQL.PaymentStatus) => void;
}

/** Used inside OrderView V1's flat action row — click-opens a button + status dropdown. */
export const OverridePaymentStatusButton: React.FC<OverridePaymentStatusButtonProps> = ({ currentStatus, onPick }) => {
  const { classes } = useStyles();
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <div className={classes.triggerWrapper} data-testid="override-payment-status-button">
          <RpiButton variant="spark" size="small" width="fit">
            Override Payment Status
          </RpiButton>
        </div>
      </Menu.Target>
      <Menu.Dropdown>
        <StatusItemList currentStatus={currentStatus} onPick={onPick} />
      </Menu.Dropdown>
    </Menu>
  );
};
